/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { ApolloClient, ApolloProvider, InMemoryCache, split } from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createUploadLink } from 'apollo-upload-client';
import { GraphQLError, parse } from 'graphql';
import { createContext, useContext, useState } from 'react';

import './index.css';
import '@assets/fonts/LemonMilkPro/styles.css';
import '@assets/fonts/SansJakarta/styles.css';

const host = process.env.REACT_APP_PUBLIC_GRAPHQL_URI;

const validateQuery = (query) => {
  try {
    parse(query); // This will throw an error if the query is invalid
  } catch (error) {
    if (error instanceof GraphQLError) {
      console.error(`[Syntax error]: ${error.message}`);
    } else {
      console.error(`[Error]: ${error.message}`);
    }
  }
};

const parseHeaders = (rawHeaders: any) => {
  const headers = new Headers();
  const preProcessedHeaders = rawHeaders.replace(/\r?\n[\t ]+/g, ' ');
  preProcessedHeaders.split(/\r?\n/).forEach((line: any) => {
    const parts = line.split(':');
    const key = parts.shift().trim();
    if (key) {
      const value = parts.join(':').trim();
      headers.append(key, value);
    }
  });
  return headers;
};

export const uploadFetch = (url: string, options: any) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = () => {
      const opts: any = {
        status: xhr.status,
        statusText: xhr.statusText,
        headers: parseHeaders(xhr.getAllResponseHeaders() || ''),
      };
      opts.url = 'responseURL' in xhr ? xhr.responseURL : opts.headers.get('X-Request-URL');
      const body = 'response' in xhr ? xhr.response : (xhr as any).responseText;
      resolve(new Response(body, opts));
    };
    xhr.onerror = () => {
      reject(new TypeError('Network request failed'));
    };
    xhr.ontimeout = () => {
      reject(new TypeError('Network request failed'));
    };
    xhr.open(options.method, url, true);

    Object.keys(options.headers).forEach(key => {
      xhr.setRequestHeader(key, options.headers[key]);
    });

    if (xhr.upload) {
      xhr.upload.onprogress = options.onProgress;
    }

    options.onAbortPossible?.(() => {
      xhr.abort();
    });

    xhr.send(options.body);
  });

const customFetch = () => {
  return async (uri: any, options: any) => {
    if (options.useUpload) {
      return uploadFetch(uri, options);
    }
    // Use default fetch from the environment (window.fetch)
    return fetch(uri, options);
  };
};


const createApolloClient = (token: string | null, proId: string | null = null) => {
  const handleUnauthenticated = () => {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('selectedProId');

    if (window.location.pathname !== '/login') {
      window.location.replace('/login');
    }
  };

  const authLink = setContext((_, { headers }) => {
    // Build authorization headers
    const authHeaders: any = {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    };

    // Auto-inject proId for Upercut users
    if (proId) {
      authHeaders['x-pro-id'] = proId;
    }

    return { headers: authHeaders };
  });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    let isUnauthenticatedError = false;

    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, extensions }) => {
        if (extensions?.code === 'GRAPHQL_FORBIDDEN') {
          console.warn('Permission denied:', message);
        }
        if (extensions?.code === 'UNAUTHENTICATED' || message?.toLowerCase().includes('jwt expired')) {
          isUnauthenticatedError = true;
        }
        if (message.includes("This account is deactivated")) {
          if (typeof window !== 'undefined') {
            localStorage.removeItem('authToken');
            window.location.href = '/';
          }
        }
      });
    }
    if (networkError) {
      console.warn('Network error:', networkError);

      const networkResultErrors = (networkError as any)?.result?.errors;
      if (Array.isArray(networkResultErrors)) {
        const hasUnauthenticatedNetworkError = networkResultErrors.some((error: any) => {
          const code = error?.extensions?.code;
          const errorMessage = error?.message;
          return code === 'UNAUTHENTICATED' || errorMessage?.toLowerCase().includes('jwt expired');
        });

        if (hasUnauthenticatedNetworkError) {
          isUnauthenticatedError = true;
        }
      }
    }

    if (isUnauthenticatedError) {
      handleUnauthenticated();
    }
  });

  // Use createUploadLink for file uploads
  const uploadLink = createUploadLink({
    uri: host,
    fetch: customFetch() as any
  });

  const wsLink = new GraphQLWsLink(
    createClient({
      url: process.env.REACT_APP_WS_GRAPHQL_URI,
      connectionParams: {
        authorization: token ? `Bearer ${token}` : '',
        ...(proId ? { 'x-pro-id': proId } : {}),
      },
      retryAttempts: 10,
      shouldRetry: () => true,
    }),
  );

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    authLink.concat(errorLink).concat(uploadLink),
  );

  return new ApolloClient({
    cache: new InMemoryCache(),
    link: splitLink,
  });
};

interface ApolloClientContextValue {
  client: ApolloClient<any>;
  updateClient: (token: string, proId?: string | null) => void;
  updateProIdToClient: (proId?: string | null) => void;
}

const ApolloClientContext = createContext<ApolloClientContextValue>({
  client: createApolloClient(
    localStorage.getItem('authToken'),
    localStorage.getItem('selectedProId')
  ),
  updateClient: (_token: string, _proId?: string | null) => { },
  updateProIdToClient: () => { },
});

export const useApolloClient = () => {
  return useContext(ApolloClientContext);
};

export const ApolloClientProvider = ({ children }) => {
  const [client, setClient] = useState(
    createApolloClient(
      localStorage.getItem('authToken'),
      localStorage.getItem('selectedProId')
    )
  );

  const updateClient = (newToken: string, newProId?: string | null) => {
    const proIdToUse = newProId !== undefined ? newProId : localStorage.getItem('selectedProId');
    setClient(createApolloClient(newToken, proIdToUse));
  };

  const updateProIdToClient = (newProId?: string | null) => {
    const token = localStorage.getItem('authToken');
    setClient(createApolloClient(token, newProId !== undefined ? newProId : localStorage.getItem('selectedProId')));
  }

  return (
    // @ts-ignore
    <ApolloClientContext.Provider value={{ client, updateClient, updateProIdToClient }}>
      <ApolloProvider client={client}>{children}</ApolloProvider>
    </ApolloClientContext.Provider>
  );
};

/** Creates a client using the current localStorage tokens. Accepts unused args for API compatibility. */
export const initializeApollo = (_initialState = null, _ctx = null) =>
  createApolloClient(
    localStorage.getItem('authToken'),
    localStorage.getItem('selectedProId'),
  );

// Example function to execute a query with validation
export const executeQuery = async (client, query, variables) => {
  validateQuery(query); // Validate the query before executing it
  return client.query({ query, variables });
};
